import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react';
import T from 'prop-types';
import { isEmpty, pick, isEqual, isFunction, mapValues, uniqBy, has } from 'lodash-es';

import UpsertExportForm from './components/UpsertExportForm';
import FormActions from './components/UpsertExportForm/FormActions';
import SaveChangesModal from './components/Modals/SaveChangesModal';
import SubmitConfirmationModal from './components/Modals/SubmitConfirmationModal';
import SaveExportSettingsModal from './components/Modals/SaveExportSettingsModal';

import Button from '../Buttons/Button';
import { useRefValue } from '../../hooks';

import api from './api';
import { groupReportEntities } from './configs/report';
import { DATA_TYPE, FIELDS_TO_VALIDATE, SETTINGS_FIELDS, DEFAULT_REPORT, ADVANCED_METRIC_MIN_DATE, MPI_MIN_DATE } from './configs/constants';
import { redirectToReportsAndExports } from './utils';
import { appStore } from '../../utils/helpers';
import { isBefore } from '../../utils/date';
import { toast } from '../Toasts';

const prepareEntities = entities => (
  entities.map(entity => ({
    ...pick(entity, 'ref_no', 'kind', 'value'),
    ...(entity.store && { store: appStore(entity.store) }),
    kind: entity?.kind?.replace('unified_', ''),
  })));

const UpsertCustomExportForm = ({
  initParams,
  formMode,
  isModal = true,
  prefetchingEntities,
  onDataTypeChange,
  onClose,
  onInitialLoad,
}) => {
  const { permissions, advancedTrialCompanyId } = initParams;
  const [dataType, setDataType] = useState(initParams.dataType);
  const [reportState, setReportState] = useState(initParams.report);
  const [pending, setPending] = useState(false);

  const [showCancelConfirmation, setCancelConfirmation] = useState(false);
  const [showSubmitConfirmation, setSubmitConfirmation] = useState(false);
  const [isUpsertModalOpen, setUpsertModalOpen] = useState(false);

  const dataTypeRef = useRefValue(dataType);
  const reportStateRef = useRefValue(reportState);
  const report = reportState[dataType];

  const enhancedReport = useMemo(() => ({
    ...report,
    ...groupReportEntities(report, { advancedTrialCompanyId }),
  }), [report, advancedTrialCompanyId]);

  const isReportChanged = useMemo(() => (formMode.isCreate
    ? !isEmpty(report.entities)
    : !isEqual(initParams.report[initParams.dataType], report)
  ), [initParams, formMode.isCreate, report]);

  const isSubmitDisabled = (() => {
    let disabled = pending || !isReportChanged
      || FIELDS_TO_VALIDATE[dataType].some(field => isEmpty(enhancedReport[field]));

    const crossAppSelected = !isEmpty(enhancedReport.data_points?.filter(item => item.startsWith('cross_app')));

    if (dataType === DATA_TYPE.ADVANCED) {
      disabled = disabled
        || isBefore(enhancedReport.start_date, ADVANCED_METRIC_MIN_DATE)
        || (enhancedReport.data_points.includes('time_spent_segment_penetration') && isEqual(enhancedReport.user_segments, ['all_users']))
        || (crossAppSelected && !enhancedReport.user_segments.some(a => a === 'all_users'))
        || (crossAppSelected && isEmpty(enhancedReport.cross_apps_pairs));
    }

    if (dataType === DATA_TYPE.MPI) {
      disabled = disabled || isBefore(enhancedReport.start_date, MPI_MIN_DATE);
    }

    return disabled;
  })();

  const isSaveDisabled = report.data_type === DATA_TYPE.ADVANCED
    && permissions.advancedTrialRequest;

  const setReport = useCallback((args) => {
    setReportState((prevState) => {
      if (isFunction(args)) {
        return {
          ...prevState,
          [dataTypeRef.current]: args(prevState[dataTypeRef.current]),
        };
      }

      return {
        ...prevState,
        [dataTypeRef.current]: { ...prevState[dataTypeRef.current], ...args },
      };
    });
  }, []);

  const closeCancelConfirmation = useCallback(() => {
    setCancelConfirmation(false);
  }, []);

  const closeSubmitConfirmation = useCallback(() => {
    setSubmitConfirmation(false);
  }, []);

  const handleSubmit = useCallback(({ action = 'create', should_redirect = true, ...restParams }) => {
    if (isSubmitDisabled) return;

    const { id, ...restReport } = report;

    let exportRequestData = {
      ...restReport,
      entities: prepareEntities(report.entities),
      ...restParams,
    };

    if (report.data_type === DATA_TYPE.MPI) {
      exportRequestData = {
        ...exportRequestData,
        mpi_data_points: uniqBy([...restReport.mpi_data_points, ...report.entities.map(e => e.mpi)], 'id'),
        entities: prepareEntities(report.entities.filter(entity => entity.has_mpi)),
      };
    }

    const requestData = {
      custom_export: exportRequestData,
    };

    const request = action === 'create'
      ? api.createCustomExport(requestData)
      : api.updateCustomExport(id, requestData);

    setPending(true);

    request.then(() => {
      if (should_redirect) redirectToReportsAndExports('?export_generating_notify=true');
      else {
        if (onClose) onClose();
        toast.success('Export request submitted. You will be notified when it is ready for download.');
      }
    }).catch(() => {
      setPending(false);
      toast.error('Oops! Something went wrong. Please, try again later');
    });
  }, [report, isSubmitDisabled]);

  const handleGenerate = useCallback(() => {
    setSubmitConfirmation(true);
  }, []);

  const handlePrefillReport = useCallback((exportData) => {
    let prefilledData = pick(exportData, SETTINGS_FIELDS[dataTypeRef.current]);

    if (dataTypeRef.current === DATA_TYPE.MPI) {
      // Temporarily until API is ready
      prefilledData = {
        ...prefilledData,
        data_granularity: 'quarterly',
      };
    } else {
      prefilledData = {
        ...prefilledData,
        data_granularity: prefilledData.data_granularity === 'weekly' ? 'daily' : prefilledData.data_granularity,
      };
    }

    setReportState(prevState => ({
      ...prevState,
      [dataTypeRef.current]: {
        ...prevState[dataTypeRef.current],
        ...prefilledData,
      },
    }));
  }, []);

  useEffect(() => {
    if (onInitialLoad) {
      onInitialLoad({
        dataType: initParams.dataType,
        state: reportStateRef.current,
        setState: setReportState,
      });
    }
  }, []);

  const handleDataTypeChange = useCallback((nextDataType) => {
    // Sync report fields between data types, but reset rest settings
    setReportState(prevState => mapValues(DEFAULT_REPORT, defaultReport => ({
      ...defaultReport,
      ...pick(prevState[dataTypeRef.current], [
        'id',
        'name',
        'entities',
        'end_date',
        'start_date',
      ]),
      ...((nextDataType === DATA_TYPE.ADVANCED && permissions.advancedTrialRequest && initParams.advancedMaxDate) ? {
        end_date: initParams.advancedMaxDate,
      } : {}),
    })));

    onDataTypeChange?.({
      prev: dataTypeRef.current,
      next: nextDataType,
    }, { state: reportStateRef.current, setState: setReportState });

    setDataType(nextDataType);
  }, []);

  const handleReset = useCallback((reportId) => {
    setReport({
      ...DEFAULT_REPORT[dataTypeRef.current],
      id: reportId || null,
    });
  }, []);

  const handleOpenNameModal = () => {
    setUpsertModalOpen(true);
    setSubmitConfirmation(false);
  };

  const handleRenameSuccess = (newReport) => {
    setReport(newReport);
    setUpsertModalOpen(false);
    handleSubmit({ action: 'create', one_time: false, should_redirect: true, name: newReport.name });
  };

  const onSaveChanges = () => {
    closeCancelConfirmation();
    setSubmitConfirmation(true);
  };

  const fetchCompanyMpiData = useCallback((companyIds) => {
    api.fetchCompanyMpiData(companyIds)
      .then(({ data }) => {
        setReportState(prevState => ({
          ...prevState,
          [dataTypeRef.current]: {
            ...prevState[dataTypeRef.current],
            entities: prevState[dataTypeRef.current].entities.map((entity) => {
              if (companyIds.includes(entity.ref_no)) {
                return {
                  ...entity,
                  has_mpi: !!data[entity.ref_no],
                  mpi: data[entity.ref_no] || null,
                };
              }
              return entity;
            }),
          },
        }));
      });
  }, []);

  const formActions = (
    <FormActions
      formMode={formMode.isEdit ? 'update' : 'create'}
      isSubmitDisabled={isSubmitDisabled}
      isSaveDisabled={isSaveDisabled}
      isModal={isModal}
      onSubmit={handleSubmit}
      onOpenModalConfirm={handleGenerate}
      onOpenNameModal={handleOpenNameModal}
    />
  );

  useEffect(() => {
    if (dataType === DATA_TYPE.MPI) {
      const requestCompanyIds = enhancedReport.entities
        .filter(entity => entity.kind === 'company' && !has(entity, 'has_mpi'))
        .map(entity => entity.ref_no);

      if (requestCompanyIds.length > 0) fetchCompanyMpiData(requestCompanyIds);
    }
  }, [dataType]);

  useEffect(() => {
    if (!isEqual(initParams.report[initParams.dataType]?.name, report.name)) {
      setReportState(prevState => ({
        ...prevState,
        [dataTypeRef.current]: {
          ...prevState[dataTypeRef.current],
          name: initParams.report[initParams.dataType]?.name,
        },
      }));
    }
  }, [initParams.report]);

  return (
    <Fragment>
      <UpsertExportForm
        initParams={initParams}
        formMode={formMode}
        dataType={dataType}
        report={enhancedReport}
        autoFocus={!isModal}
        formActions={isModal ? null : formActions}
        prefetchingEntities={prefetchingEntities}
        onReset={handleReset}
        onPrefill={handlePrefillReport}
        onChange={setReport}
        onChangeDataType={handleDataTypeChange}
      />
      {isModal && (
        <div className="global-upsert-export-modal__footer">
          <div className="global-upsert-export-modal__footer-buttons">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          {formActions}
        </div>
      )}

      {showCancelConfirmation && (
        <SaveChangesModal
          open={showCancelConfirmation}
          title={formMode.isEdit ? 'Save changes?' : 'Leave without exporting?'}
          description={formMode.isEdit
            ? 'There are unsaved changes in this export'
            : 'You will lose your selections and settings.'}
          confirmButtonText={formMode.isEdit ? 'Save changes' : 'Close and keep editing'}
          onConfirm={formMode.isEdit ? onSaveChanges : closeCancelConfirmation}
          onClose={closeCancelConfirmation}
        />
      )}

      {!isModal && showSubmitConfirmation && (
        <SubmitConfirmationModal
          open={showSubmitConfirmation}
          action={formMode.isEdit ? 'update' : 'create'}
          onClose={closeSubmitConfirmation}
          onConfirm={handleSubmit}
        />
      )}
      {((isModal && showSubmitConfirmation) || isUpsertModalOpen) && (
        <SaveExportSettingsModal
          open={showSubmitConfirmation || isUpsertModalOpen}
          report={enhancedReport}
          onConfirm={isUpsertModalOpen ? handleRenameSuccess : handleSubmit}
          onClose={isUpsertModalOpen ? () => setUpsertModalOpen(false) : closeSubmitConfirmation}
        />
      )}
    </Fragment>
  );
};

UpsertCustomExportForm.propTypes = {
  initParams: T.shape({
    maxDate: T.string,
    advancedMaxDate: T.string,
    maxPeriod: T.number,
    dataType: T.string,
    report: T.object,
    permissions: T.object,
    advancedTrialCompanyId: T.string,
  }),
  formMode: T.object,
  isModal: T.bool,
  prefetchingEntities: T.bool,
  onInitialLoad: T.func,
  onDataTypeChange: T.func,
  onClose: T.func,
};

export default UpsertCustomExportForm;
